<template>
    <PaginatedList :getListMethod="getMachines" :optional-filers="{}" :paginationEnabled="true" :itemsPerPage="30"
        :grid="cardMode" :open-function="openItem" :pk="pk" :headerheight="listHeaderHeight" ref="paginatedList">
        <template v-slot:header>
            <div class="equipment-header">
                <div class="equipment-title-block">
                    <h2>{{ $t("text.your_equipment") }}</h2>
                    <div class="equipment-summary">
                        <span>{{ totalMachineCount }} {{ $t("text.equipment").toLowerCase() }}</span>
                    </div>
                </div>

                <div class="equipment-actions">
                    <v-btn-group variant="outlined" divided density="comfortable">
                        <v-btn :icon="'mdi-view-grid-outline'" :color="cardMode ? 'primary' : undefined"
                            :title="$t('text.displaymode')" @click="cardMode = true">
                        </v-btn>
                        <v-btn :icon="'mdi-view-list-outline'" :color="!cardMode ? 'primary' : undefined"
                            :title="$t('text.displaymode')" @click="cardMode = false">
                        </v-btn>
                    </v-btn-group>
                    <v-btn icon="mdi-refresh" variant="text" :title="$t('text.refresh')" @click="refreshList">
                    </v-btn>
                </div>
            </div>
        </template>

        <template v-slot:list-item="{ item }">
            <CustomerMachineCardItem v-if="cardMode" :item="item" :show-price="showPrice" :machine-title="machineTitle"
                :machine-subtitle="machineSubtitle" :format-date="formatDate" :format-amount="formatAmount"
                @open-item="openItem" @open-panel="openPanel">
            </CustomerMachineCardItem>
            <CustomerMachineRowItem v-else :item="item" :machine-title="machineTitle"
                :machine-subtitle="machineSubtitle" @open-item="openItem">
            </CustomerMachineRowItem>
        </template>

        <template v-slot:footer>
            <Footer />
        </template>
    </PaginatedList>
</template>


<script>
import http from "@/api/client";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";
import Footer from "@/components/Footer.vue";
import CustomerMachineCardItem from "@/components/Cards/CustomerMachineCardItem.vue";
import CustomerMachineRowItem from "@/components/Cards/CustomerMachineRowItem.vue";
import PaginatedList from "@/components/PaginatedList.vue";

export default {
    name: "MachineList",
    mixins: [AuthStoreMixin],
    components: {
        PaginatedList,
        CustomerMachineCardItem,
        CustomerMachineRowItem,
        Footer,
    },

    data() {
        return {
            pk: "",
            machineTypes: [],
            machineSuppliers: [],
            parentMachineModels: [],
            machines: [],
            totalMachineCount: 0,
            loading: false,
            search: "",
            cardMode: true,
            jobCountRequests: new Set(),
        };
    },

    mounted() { },

    methods: {
        async getMachines(filter) {
            this.loading = true;
            const page = filter && filter.Page ? filter.Page : 1;
            const pageItems = await http()
                .get(
                    "api2/customer/machines/filtered" +
                    this.serializeToQueryParams(filter)
                )
                .then((response) => {
                    this.totalMachineCount = response.data.Total || 0;
                    const items = response.data.Items || [];
                    this.machines = page === 1 ? items : this.machines.concat(items);
                    this.machineSuppliers = this.uniqueSorted(
                        this.machines.map((e) => e.SupplierName)
                    );
                    this.machineTypes = this.uniqueSorted(
                        this.machines.map((e) => e.MachineType)
                    );
                    this.parentMachineModels = this.uniqueSorted(
                        this.machines
                            .map((e) => e.ParentMachineModelName)
                            .filter(Boolean)
                    );
                    return items;
                })
                .finally(() => {
                    this.loading = false;
                });

            this.refreshJobCounts(pageItems);
            return pageItems;
        },

        uniqueSorted(values) {
            return values
                .filter(Boolean)
                .filter((value, index, array) => array.indexOf(value) === index)
                .sort();
        },

        refreshJobCounts(items) {
            (items || []).forEach((item) => this.getJobCounts(item));
        },

        refreshList() {
            this.jobCountRequests.clear();
            if (this.$refs.paginatedList) {
                this.$refs.paginatedList.fetch();
            }
        },

        getJobCounts(item) {
            if (!item || !item.Id || this.jobCountRequests.has(item.Id)) {
                return item;
            }

            this.jobCountRequests.add(item.Id);
            http()
                .get("api2/customer/machines/openJobsCount/" + item.Id)
                .then((response) => {
                    const activePartsJobs = response.data.ActivePartJobsCount;
                    const activeServiceJobs = response.data.ActiveOtherJobsCount;
                    Object.assign(item, {
                        ActivePartsJobs: activePartsJobs,
                        ActiveServiceJobs: activeServiceJobs,
                    });

                    const itemIndex = this.machines.findIndex((machine) => machine.Id === item.Id);
                    if (itemIndex >= 0 && this.machines[itemIndex] !== item) {
                        Object.assign(this.machines[itemIndex], {
                            ActivePartsJobs: activePartsJobs,
                            ActiveServiceJobs: activeServiceJobs,
                        });
                    }
                });
            return item;
        },

        machineTitle(item) {
            if (!item) {
                return "";
            }

            return item.DisplayName || [item.MachineModelName, item.Name].filter(Boolean).join(" ") || this.$t("text.equipment");
        },

        machineSubtitle(item) {
            const values = [item && item.SupplierName, item && item.MachineType, item && item.MachineModelName]
                .filter(Boolean)
                .filter((value, index, array) => array.indexOf(value) === index);

            return values.length ? values.join(" / ") : "-";
        },

        formatDate(dateString) {
            if (!dateString) {
                return null;
            }

            const date = new Date(dateString);
            if (Number.isNaN(date.getTime())) {
                return null;
            }

            return new Intl.DateTimeFormat(undefined, {
                year: "numeric",
                month: "short",
                day: "2-digit",
            }).format(date);
        },

        formatAmount(amount) {
            if (amount === undefined || amount === null) {
                return "-";
            }

            return this.formatCurrencyTotal(amount);
        },

        openPanel(item, routeName) {
            this.$router.push({ name: routeName, params: { id: item.Id } });
        },

        openItem(item) {
            this.$router.push({ name: "ViewMachine", params: { id: item.Id } });
        },
    },

    computed: {
        showPrice() {
            return this.canShowPrices;
        },

        listHeaderHeight() {
            return this.isPhoneBool ? 132 : 92;
        },
    },

    watch: {},
};
</script>

<style lang="scss" scoped>
.equipment-header {
    position: relative;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 88px;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(24, 39, 75, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f7f9fb 100%);
}

.equipment-title-block {
    min-width: 0;

    h2 {
        margin: 0;
        color: rgba(25, 34, 46, 0.92);
        font-size: 22px;
        font-weight: 700;
        line-height: 1.25;
    }
}

.equipment-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    color: rgba(25, 34, 46, 0.64);
    font-size: 13px;
    line-height: 1.3;

    span {
        display: inline-flex;
        align-items: center;
        min-height: 24px;
        padding: 2px 8px;
        border: 1px solid rgba(24, 39, 75, 0.1);
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.78);
    }
}

.equipment-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

@media (max-width: 720px) {
    .equipment-header {
        align-items: flex-start;
        min-height: 126px;
        flex-direction: column;
        padding: 12px;
    }

    .equipment-actions {
        width: 100%;
        justify-content: flex-end;
    }

}
</style>
